/**
 * TanStack Query hooks for the clinical-admin dashboard.
 *
 * Per-tab queries are keyed `['admin','clinical-admin','patient',id,<tab>]`
 * with a 60s staleTime — switching away from a tab and back, or reopening
 * the same customer, serves from cache instantly and only revalidates in
 * the background. Each tab fetches lazily on first open.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';
import type { AdminLabOrdersResponse } from '@/types/adminLabs';
import type { CheckinDoseHistoryResponse } from '@/types/checkinDoseHistory';
import type {
  AdminDocumentPurpose,
  IdInfo,
  AdminUploadCompleteResponse,
  AdminUploadUrlResponse,
  AgentMessageTurn,
  AgentPendingAction,
  AgentThreadDetail,
  AgentThreadSummary,
  BillingResponse,
  DocumentsResponse,
  EmrPatientResponse,
  EmrUpdateBody,
  FunnelResponse,
  HealthieTask,
  IntakePreviewResponse,
  IntakeResponse,
  LifecycleResponse,
  OrderableMedication,
  RescriptOrderResult,
  RescriptTargetsResponse,
  VerifyIntakeResult,
  OrdersResponse,
  PatientCard,
  PatientListResponse,
  PatientSearchResponse,
  PrescriptionsResponse,
  RetryRoutingResponse,
  SubscriptionActionResponse,
  SubscriptionSwitchOption,
  SubscriptionSwitchResponse,
  SwitchProductRequest,
  SwitchProductResponse,
} from '@/types/clinicalAdmin';

const BASE = '/admin/clinical-admin';

interface PatientListArgs {
  search?: string;
  status?: string;
  partnerKey?: string;
  productId?: string;
  state?: string;
  hasWarnings?: boolean;
  joinedAfter?: string;
  joinedBefore?: string;
  /** A storefront slug or id, `any` (any store) or `none` (house only). */
  storefront?: string;
  offset?: number;
  limit?: number;
}

export function useClinicalPatients({
  search,
  status,
  partnerKey,
  productId,
  state,
  hasWarnings,
  joinedAfter,
  joinedBefore,
  storefront,
  offset = 0,
  limit = 50,
}: PatientListArgs = {}) {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  if (partnerKey) params.set('partner_key', partnerKey);
  if (productId) params.set('product_id', productId);
  if (state) params.set('state', state);
  if (hasWarnings) params.set('has_warnings', 'true');
  if (joinedAfter) params.set('joined_after', joinedAfter);
  if (joinedBefore) params.set('joined_before', joinedBefore);
  if (storefront) params.set('storefront', storefront);
  return useQuery({
    queryKey: [
      'admin',
      'clinical-admin',
      'patients',
      search ?? '',
      status ?? '',
      partnerKey ?? '',
      productId ?? '',
      state ?? '',
      hasWarnings ?? false,
      joinedAfter ?? '',
      joinedBefore ?? '',
      storefront ?? '',
      offset,
      limit,
    ],
    queryFn: () =>
      apiFetch<PatientListResponse>(`${BASE}/patients?${params}`),
    staleTime: 30_000,
  });
}

export function useClinicalFunnel(
  dateFrom: string,
  dateThrough: string,
  enabled = true,
) {
  const params = new URLSearchParams({
    date_from: dateFrom,
    date_through: dateThrough,
  });
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'funnel', dateFrom, dateThrough],
    queryFn: () => apiFetch<FunnelResponse>(`${BASE}/funnel?${params}`),
    enabled: enabled && Boolean(dateFrom) && Boolean(dateThrough),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useClinicalPatientSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'search', query],
    queryFn: () =>
      apiFetch<PatientSearchResponse>(
        `${BASE}/patients/search?q=${encodeURIComponent(query)}`,
      ),
    enabled: enabled && query.trim().length > 0,
    staleTime: 30_000,
  });
}

export function useClinicalPatientSummary(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'summary'],
    queryFn: () =>
      apiFetch<PatientCard>(`${BASE}/patients/${patientId}/summary`),
    enabled,
    staleTime: 30_000,
  });
}

export function useClinicalPatientLifecycle(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'lifecycle'],
    queryFn: () =>
      apiFetch<LifecycleResponse>(`${BASE}/patients/${patientId}/lifecycle`),
    enabled,
    staleTime: 30_000,
  });
}

export function useClinicalPatientEmr(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'emr'],
    queryFn: () =>
      apiFetch<EmrPatientResponse>(`${BASE}/patients/${patientId}/emr`),
    enabled,
    staleTime: 60_000,
  });
}

export function useClinicalPatientPrescriptions(
  patientId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'prescriptions'],
    queryFn: () =>
      apiFetch<PrescriptionsResponse>(
        `${BASE}/patients/${patientId}/prescriptions`,
      ),
    enabled,
    staleTime: 60_000,
  });
}

/** Mint a fresh signed URL for a rendered Rx PDF (a PDF-type eRx row).
 *  Called on click so the short-lived URL is never stale in the cached
 *  prescriptions list. Returns null if the PDF can't be signed. */
export async function fetchPrescriptionPdfUrl(
  patientId: string,
  pdfId: string,
): Promise<string | null> {
  const res = await apiFetch<{ url: string | null }>(
    `${BASE}/patients/${patientId}/prescription-pdfs/${pdfId}/url`,
  );
  return res.url ?? null;
}

// ── Clinical-ops AI agent ────────────────────────────────────────────────────

const agentThreadsKey = ['admin', 'clinical-admin', 'agent', 'threads'] as const;
const agentThreadsListKey = (query: string) =>
  [...agentThreadsKey, { q: query }] as const;
const agentThreadKey = (id: string) =>
  ['admin', 'clinical-admin', 'agent', 'thread', id] as const;

/** List the calling admin's own agent threads. */
export function useAgentThreads(query = '', enabled = true) {
  const q = query.trim();
  const qs = q ? `?${new URLSearchParams({ q }).toString()}` : '';
  return useQuery({
    queryKey: agentThreadsListKey(q),
    queryFn: () => apiFetch<AgentThreadSummary[]>(`${BASE}/agent/threads${qs}`),
    enabled,
    staleTime: 30_000,
  });
}

/** One thread's messages + any pending actions. */
export function useAgentThread(threadId: string | null) {
  return useQuery({
    queryKey: agentThreadKey(threadId ?? 'none'),
    queryFn: () =>
      apiFetch<AgentThreadDetail>(`${BASE}/agent/threads/${threadId}`),
    enabled: !!threadId,
  });
}

/** Create a new (empty) thread; returns its id. */
export function useCreateAgentThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ thread_id: string }>(`${BASE}/agent/threads`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentThreadsKey }),
  });
}

/** Send a message turn. `model: 'opus'` escalates this turn's reasoning.
 *  Optimistically appends the user's message so it shows immediately. */
export function useSendAgentMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { content: string; model?: 'opus' | null }) =>
      apiFetch<AgentMessageTurn>(`${BASE}/agent/threads/${threadId}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: agentThreadKey(threadId) });
      const prev = qc.getQueryData<AgentThreadDetail>(agentThreadKey(threadId));
      qc.setQueryData<AgentThreadDetail>(agentThreadKey(threadId), (old) =>
        old
          ? {
              ...old,
              messages: [
                ...old.messages,
                {
                  id: `optimistic-${Date.now()}`,
                  role: 'user',
                  content: body.content,
                  content_text: body.content,
                  created_at: new Date().toISOString(),
                },
              ],
            }
          : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(agentThreadKey(threadId), ctx.prev);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentThreadKey(threadId) });
      qc.invalidateQueries({ queryKey: agentThreadsKey });
    },
  });
}

/** Approve (with required reason) or reject a proposed write action. */
export function useConfirmAgentAction(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actionId,
      decision,
      reason,
      reasonCode,
    }: {
      actionId: string;
      decision: 'approve' | 'reject';
      reason?: string;
      // Required on approve for the 4 order-action tools (cancel_order/
      // refund_charge/reship_order/{refire_order,rescript_order,
      // update_order_sig}) — see ORDER_ACTION_TOOL_MAP. Omitted for every
      // other tool, which keeps the plain free-text reason.
      reasonCode?: string;
    }) =>
      apiFetch<AgentPendingAction>(
        `${BASE}/agent/actions/${actionId}/confirm`,
        {
          method: 'POST',
          body: JSON.stringify({ decision, reason, reason_code: reasonCode }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: agentThreadKey(threadId) });
      qc.invalidateQueries({ queryKey: agentThreadsKey });
    },
  });
}

/** Rename a thread. */
export function useRenameAgentThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      apiFetch<AgentThreadSummary>(`${BASE}/agent/threads/${threadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: agentThreadsKey });
      qc.invalidateQueries({ queryKey: agentThreadKey(vars.threadId) });
    },
  });
}

/** Delete a thread (cascades its messages + pending actions). */
export function useDeleteAgentThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      apiFetch<{ deleted: boolean }>(`${BASE}/agent/threads/${threadId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: agentThreadsKey }),
  });
}

// Single helper because all 3 task mutations want the same query-key
// invalidation — the Write-Rx tasks list lives on the prescriptions
// endpoint, so any task change has to refetch it.
function invalidatePrescriptions(qc: ReturnType<typeof useQueryClient>, patientId: string) {
  qc.invalidateQueries({
    queryKey: [
      'admin',
      'clinical-admin',
      'patient',
      patientId,
      'prescriptions',
    ],
  });
}

/** Clinical-ops creates a one-off Write-Rx task in Hinds' Healthie queue.
 *  Invalidates the prescriptions query so the new task shows up in the
 *  Write-Rx tasks card without a manual refresh. */
export function useCreateClinicalRxTask(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { content: string; template_id?: string | null }) =>
      apiFetch<HealthieTask>(
        `${BASE}/patients/${patientId}/prescriptions/tasks`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => invalidatePrescriptions(qc, patientId),
  });
}

/** Edit an existing Write-Rx task's body. */
export function useUpdateClinicalRxTask(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      content,
    }: {
      taskId: string;
      content: string;
    }) =>
      apiFetch<{ id: string; content: string | null }>(
        `${BASE}/patients/${patientId}/prescriptions/tasks/${taskId}`,
        { method: 'PATCH', body: JSON.stringify({ content }) },
      ),
    onSuccess: () => invalidatePrescriptions(qc, patientId),
  });
}

/** Delete a Write-Rx task (vs marking it complete). */
export function useDeleteClinicalRxTask(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) =>
      apiFetch<{ id: string; deleted: boolean }>(
        `${BASE}/patients/${patientId}/prescriptions/tasks/${taskId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => invalidatePrescriptions(qc, patientId),
  });
}

/** Sources/targets/awaiting orders for the Clinician request modal. Nested
 *  under the prescriptions key so every task/order mutation's
 *  `invalidatePrescriptions` refreshes it too. */
/** Who this patient's prescribing work goes to (their assigned prescriber in
 *  team mode, else the default), named the way patients see them. */
export function useRxTaskAssignee(patientId: string) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'prescriptions', 'assignee'],
    queryFn: () =>
      apiFetch<{ name: string; assigned: boolean }>(
        `${BASE}/patients/${patientId}/prescriptions/assignee`,
      ),
    staleTime: 60_000,
  });
}

export function useRescriptTargets(patientId: string, enabled = true) {
  return useQuery({
    queryKey: [
      'admin',
      'clinical-admin',
      'patient',
      patientId,
      'prescriptions',
      'rescript-targets',
    ],
    queryFn: () =>
      apiFetch<RescriptTargetsResponse>(
        `${BASE}/patients/${patientId}/prescriptions/rescript-targets`,
      ),
    enabled,
    staleTime: 30_000,
  });
}

function invalidateClinicianRequestViews(
  qc: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  // A re-script creates a new pharmacy order and (usually) a Write-Rx task,
  // so the prescriptions card, the Orders tab and the summary header all
  // change at once.
  invalidatePrescriptions(qc, patientId);
  qc.invalidateQueries({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'orders'],
  });
  qc.invalidateQueries({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'summary'],
  });
}

/** Clone a fulfilled/cancelled order into a $0 awaiting order for the
 *  chosen product so it flows through the clinician worklist normally. */
export function useCreateRescriptOrder(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      source_order_id: string;
      target_product_id: string;
      reason: string;
      note?: string;
    }) =>
      apiFetch<RescriptOrderResult>(
        `${BASE}/patients/${patientId}/prescriptions/rescript-orders`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => invalidateClinicianRequestViews(qc, patientId),
  });
}

/** Audited ops override: mark an awaiting order's legacy intake gate as
 *  verified so it returns to the clinician's Ready list. */
export function useVerifyIntakeOverride(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      reason,
      evidence,
    }: {
      orderId: string;
      reason: string;
      evidence: string;
    }) =>
      apiFetch<VerifyIntakeResult>(
        `${BASE}/patients/${patientId}/prescriptions/orders/${orderId}/verify-intake`,
        { method: 'POST', body: JSON.stringify({ reason, evidence }) },
      ),
    onSuccess: () => invalidateClinicianRequestViews(qc, patientId),
  });
}

export function useClinicalPatientOrders(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'orders'],
    queryFn: () =>
      apiFetch<OrdersResponse>(`${BASE}/patients/${patientId}/orders`),
    enabled,
    staleTime: 60_000,
  });
}

export function useClinicalPatientLabOrders(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'lab-orders'],
    queryFn: () => apiFetch<AdminLabOrdersResponse>(`${BASE}/patients/${encodeURIComponent(patientId)}/lab-orders`),
    // Owned admin records remain readable independently of new-sales cohorts.
    enabled: enabled && Boolean(patientId),
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: false,
  });
}

/** Resend a kit Tasso refused on its USPS address check (Tasso ships FedEx,
 *  so some deliverable addresses fail it). The backend records who and why. */
export function useApproveLabAddressOverride(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      note,
      patientConfirmed,
    }: {
      orderId: string;
      note: string;
      patientConfirmed: boolean;
    }) =>
      apiFetch<{ dispatch_status: string }>(
        `${BASE}/patients/${encodeURIComponent(patientId)}/lab-orders/${encodeURIComponent(orderId)}/address-override`,
        {
          method: 'POST',
          body: JSON.stringify({ note, patient_confirmed: patientConfirmed }),
        },
      ),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'lab-orders'],
      }),
  });
}

export function useClinicalPatientBilling(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'billing'],
    queryFn: () =>
      apiFetch<BillingResponse>(`${BASE}/patients/${patientId}/billing`),
    enabled,
    staleTime: 60_000,
  });
}

export function useClinicalPatientIntake(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'intake'],
    queryFn: () =>
      apiFetch<IntakeResponse>(`${BASE}/patients/${patientId}/intake`),
    enabled,
    staleTime: 60_000,
  });
}

/** Monthly check-ins, dose and stack requests, and the prescriber's answer. */
export function useClinicalCheckinDoseHistory(patientId: string) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'checkin-dose-history'],
    queryFn: () =>
      apiFetch<CheckinDoseHistoryResponse>(
        `${BASE}/patients/${patientId}/checkin-dose-history`,
      ),
    staleTime: 60_000,
  });
}

/** Labeled intake answers + clinical highlights, the way the clinician sees
 *  them. Scoped to one order's required intake when `orderId` is given. */
export function useClinicalPatientIntakePreview(
  patientId: string | null,
  orderId?: string | null,
) {
  return useQuery({
    queryKey: [
      'admin',
      'clinical-admin',
      'patient',
      patientId,
      'intake-preview',
      orderId ?? 'all',
    ],
    queryFn: () =>
      apiFetch<IntakePreviewResponse>(
        `${BASE}/patients/${patientId}/intake-preview${
          orderId ? `?order_id=${encodeURIComponent(orderId)}` : ''
        }`,
      ),
    enabled: !!patientId,
    staleTime: 60_000,
  });
}

export function useClinicalPatientDocuments(patientId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'documents'],
    queryFn: () =>
      apiFetch<DocumentsResponse>(`${BASE}/patients/${patientId}/documents`),
    enabled,
    // Documents carry short-lived signed URLs — keep them fresh.
    staleTime: 30_000,
  });
}

export function useUpdatePatientEmr(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmrUpdateBody) =>
      apiFetch<EmrPatientResponse>(`${BASE}/patients/${patientId}/emr`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Identity edits ripple into the summary header and EMR tab.
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId],
      });
    },
  });
}

/** Retry routing on a failed/unrouted order — replays the original Stripe
 *  event through the order pipeline after the admin has fixed the underlying
 *  data gap (e.g. patient DOB). */
export function useRetryOrderRouting(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) =>
      apiFetch<RetryRoutingResponse>(
        `${BASE}/orders/${orderId}/retry-routing`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      // New order row appears + the superseded failed row updates.
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'orders'],
      });
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'summary'],
      });
    },
  });
}

/** Catalog of treatments an order can be switched TO (active
 *  pharmacy_products for the order's partner, minus the current product).
 *  Fetched when the Switch Treatment modal opens. */
export function useOrderSwitchOptions(orderId: string | null) {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'order', orderId, 'switch-options'],
    queryFn: () =>
      apiFetch<{ options: OrderableMedication[] }>(
        `${BASE}/orders/${orderId}/switch-options`,
      ),
    enabled: Boolean(orderId),
    staleTime: 5 * 60_000,
  });
}

/** Switch an awaiting_prescription order to a different product (patient
 *  changed their mind on dosage form/med, e.g. NAD nasal → injection).
 *  Backend rebuilds the med metadata from the pharmacy catalog, optionally
 *  moves the Stripe sub to the new plan price with immediate proration,
 *  then submits (if the new med is already charted) or re-tasks Hinds. */
export function useSwitchOrderProduct(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      body,
    }: {
      orderId: string;
      body: SwitchProductRequest;
    }) =>
      apiFetch<SwitchProductResponse>(
        `${BASE}/orders/${orderId}/switch-product`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      for (const key of ['orders', 'summary', 'billing', 'prescriptions']) {
        qc.invalidateQueries({
          queryKey: ['admin', 'clinical-admin', 'patient', patientId, key],
        });
      }
    },
  });
}

/** Cancel a sub, refund its latest charge, both, or pause/resume — in one
 *  hook, with the action chosen at call time. Invalidates billing on success
 *  so the row re-renders with the updated sub status / new refund row. */
export type SubscriptionActionKind =
  | 'cancel'
  | 'refund'
  | 'cancel-and-refund'
  | 'pause'
  | 'resume'
  | 'change-renewal-date';

export function useSubscriptionAction(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      action,
      subId,
      cancelPharmacyOrder,
      pauseUntil,
      newRenewalDate,
      reason,
      reasonCode,
    }: {
      action: SubscriptionActionKind;
      subId: string;
      // cancel-and-refund only: also recall the current cycle's still-
      // cancellable pharmacy order (the default-checked confirm checkbox).
      cancelPharmacyOrder?: boolean;
      // pause only: resume/next-bill date (YYYY-MM-DD) + optional reason.
      pauseUntil?: string;
      // change-renewal-date only: new next-bill date (YYYY-MM-DD). Today =
      // bills + ships now; a future date delays (a pause). + optional reason.
      newRenewalDate?: string;
      reason?: string;
      // refund / cancel-and-refund only: dropdown-selected reason_code,
      // required by the backend (order_action_log audit trail).
      reasonCode?: string;
    }) =>
      apiFetch<SubscriptionActionResponse>(
        `${BASE}/billing/subscriptions/${subId}/${action}`,
        {
          method: 'POST',
          ...(action === 'cancel-and-refund'
            ? {
                body: JSON.stringify({
                  cancel_pharmacy_order: cancelPharmacyOrder ?? true,
                  reason_code: reasonCode,
                  reason: reason || undefined,
                }),
              }
            : action === 'refund'
              ? {
                  body: JSON.stringify({
                    reason_code: reasonCode,
                    reason: reason || undefined,
                  }),
                }
              : action === 'cancel'
                ? {
                    body: JSON.stringify({
                      reason_code: reasonCode,
                      reason: reason || undefined,
                    }),
                  }
                : action === 'pause'
                  ? {
                      body: JSON.stringify({
                        pause_until: pauseUntil,
                        reason: reason ?? '',
                      }),
                    }
                  : action === 'change-renewal-date'
                    ? {
                        body: JSON.stringify({
                          new_renewal_date: newRenewalDate,
                          reason: reason ?? '',
                        }),
                      }
                    : {}),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'billing'],
      });
    },
  });
}

/** Same-treatment dosage-form choices that take effect on the selected
 * subscription's next renewal. No current shipment or charge is changed. */
export function useSubscriptionSwitchOptions(
  patientId: string,
  subId: string | null,
) {
  return useQuery({
    queryKey: [
      'admin',
      'clinical-admin',
      'patient',
      patientId,
      'subscription',
      subId,
      'switch-options',
    ],
    queryFn: () =>
      apiFetch<{ options: SubscriptionSwitchOption[] }>(
        `${BASE}/patients/${patientId}/billing/subscriptions/${subId}/switch-options`,
      ),
    enabled: Boolean(patientId && subId),
    staleTime: 60_000,
  });
}

export function useSwitchSubscriptionProduct(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subId,
      newProductId,
      note,
    }: {
      subId: string;
      newProductId: string;
      note: string;
    }) =>
      apiFetch<SubscriptionSwitchResponse>(
        `${BASE}/patients/${patientId}/billing/subscriptions/${subId}/switch-product`,
        {
          method: 'POST',
          body: JSON.stringify({
            new_product_id: newProductId,
            note,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'billing'],
      });
    },
  });
}

/**
 * Admin uploads a document on behalf of a patient (e.g. an ID the
 * patient couldn't get through the portal upload). Two-step flow,
 * mirroring `useUploadDocument` in hooks.ts:
 *
 *   1. POST admin upload-url → backend reserves a file_uploads row and
 *      returns a Supabase Storage signed PUT URL.
 *   2. PUT bytes directly to that URL (bypasses our backend so Railway
 *      bandwidth stays flat).
 *   3. POST complete → backend downloads bytes, base64-encodes, pushes
 *      to Healthie createDocument, marks ready.
 *
 * Returns a `mutate({ file, purpose })` you can wire to a file input.
 * Invalidates the documents query on success so the new row appears.
 */
export function useAdminUploadDocument(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      purpose = 'id',
    }: {
      file: File;
      purpose?: AdminDocumentPurpose;
    }): Promise<AdminUploadCompleteResponse> => {
      const meta = await apiFetch<AdminUploadUrlResponse>(
        `${BASE}/patients/${patientId}/documents/upload-url`,
        {
          method: 'POST',
          body: JSON.stringify({
            filename: file.name,
            mime: file.type || 'application/octet-stream',
            size_bytes: file.size,
            purpose,
          }),
        },
      );
      // Direct PUT to Supabase Storage. Don't use apiFetch — that prepends
      // our API base URL and adds the Bearer header, both of which break
      // Storage's signed URL.
      const putRes = await fetch(meta.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-upsert': 'false',
        },
      });
      if (!putRes.ok) {
        let detail = '';
        try {
          detail = await putRes.text();
        } catch {
          /* ignore */
        }
        throw new Error(
          `Storage PUT failed: ${putRes.status}${
            detail ? ' — ' + detail.slice(0, 200) : ''
          }`,
        );
      }
      return apiFetch<AdminUploadCompleteResponse>(
        `${BASE}/patients/${patientId}/documents/${meta.file_upload_id}/complete`,
        { method: 'POST' },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'documents'],
      });
    },
  });
}

/** ---- Missing-info flags (Healthie tag workflow) ---- */

export interface MissingInfoFlag {
  id: string;
  patient_id: string | null;
  healthie_user_id: string;
  tag_id: string;
  tag_name: string;
  detected_at: string;
  patient_email: string | null;
  patient_display_name: string | null;
}

export function useMissingInfoFlags() {
  return useQuery({
    queryKey: ['admin', 'clinical-admin', 'missing-info'],
    queryFn: () =>
      apiFetch<{ flags: MissingInfoFlag[] }>(`${BASE}/missing-info`),
    refetchInterval: 60_000,
  });
}

export function useResolveMissingInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      flagId,
      note,
      recreateTask,
    }: {
      flagId: string;
      note?: string;
      recreateTask?: boolean;
    }) =>
      apiFetch<{
        flag_id: string;
        tag_removed: boolean;
        task_recreated: boolean;
      }>(`${BASE}/missing-info/${flagId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({
          note: note ?? '',
          recreate_task: recreateTask ?? true,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'missing-info'],
      });
    },
  });
}

/** Re-run the ID vision check on the patient's latest ID upload (covers
 *  pre-feature uploads and fail-open passes). Refreshes the EMR tab. */
export function useRerunIdCheck(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<IdInfo>(`${BASE}/patients/${patientId}/id-check/rerun`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['admin', 'clinical-admin', 'patient', patientId, 'emr'],
      });
    },
  });
}

/** ---- "View as patient" — mint a one-time login link (impersonation) ---- */

export interface LoginLinkResponse {
  action_link: string;
  otp: string | null;
  email: string;
  expires_minutes: number;
}

/** Mint a one-time magic sign-in link for a patient (staff debug / "see what
 *  they see"). Does NOT email the patient. Admin-gated + audit-logged
 *  server-side. The link is single-use and expires in ~1h. */
export function useGeneratePatientLoginLink(patientId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<LoginLinkResponse>(`${BASE}/patients/${patientId}/login-link`, {
        method: 'POST',
      }),
  });
}

export interface MembershipLinkResponse {
  url: string;
  expires_days: number;
}

/** A rep's post-call System Membership link for this patient: it offers the
 *  $199 membership whatever the patient's portal arm, and records the
 *  enrollment as this rep's. */
export function useMembershipLink(patientId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<MembershipLinkResponse>(`${BASE}/patients/${patientId}/membership-link`, {
        method: 'POST',
      }),
  });
}
